import InTheHeadlight from "./inTheHeadlight";
import OurCarsFilters from "./carFilters/ourCarsFilters";
import { Car, CarPrizeProps, MakesAndStyles, OurCarsProps } from "./types";
import styles from "./ourCars.module.css";
import OurCarPrizes from "./carPrizes/ourCarPrizes";
import { access } from "../../../../../common-utils";
import { createContext, useEffect, useState } from "react";
import { getCarPrizes, getMakesAndStyles } from "../../dreamCar-service";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import { publishFilteredCars } from "../../../../../redux/features/cars/carsSlice";
import OurCarsLoader from "./ourCarsLoader";
import { useInView } from "react-intersection-observer";
import InstantWinWinBannerAndTab from "../../instantWinBannerAndTab";
import Modal from "../../../../../components/Modal/modal";
import { publishModalVisibility } from "../../../../../redux/features/modal/modalSlice";

export const CompetitionDefaultTicket = createContext({
  defaultTicketCount: 0,
  showInstantWinText: false,
});

const OurCars = ({ competitionTypeId }: { competitionTypeId: string }) => {
  const [ourCarsData, setOurCarsData] = useState<OurCarsProps>();
  const [isLoading, setIsLoading] = useState(true);
  const buyYourTicketsModalData = useAppSelector(state => state.modal.data);
  const isBuyTicketModalVisible = useAppSelector(state => state.modal.isOpen);
  const dispatch = useAppDispatch();
  const { ref, inView } = useInView();

  useEffect(() => {
    (async () => {
      try {
        const [makesAndStylesResponse, carPrizesResponse] = await Promise.all([getMakesAndStyles(), getCarPrizes()]);
        const { makeList, styleList, showFilters } = (await makesAndStylesResponse.json()) as MakesAndStyles;
        const {
          DefaultTicketCount,
          PrizeList,
          ShowInstantWinText,
          IsInstantWinActive,
          IsMobile,
          SupportsMultibuy,
          HasActiveCompetition,
          HasMultibuyClass,
        } = (await carPrizesResponse.json()) as CarPrizeProps;
        const normalizedResults = PrizeList.Results.map(car => ({
          ...car,
          MphTime: Number(String(car.MphTime).replace(",", ".")),
        }));
        setOurCarsData({
          carPrizes: {
            DefaultTicketCount,
            PrizeList: {
              ...PrizeList,
              Results: normalizedResults,
            },
            ShowInstantWinText,
            IsInstantWinActive,
            IsMobile,
            SupportsMultibuy,
            HasActiveCompetition,
            HasMultibuyClass,
          },
          makeList,
          styleList,
          showFilters,
        });
        dispatch(
          publishFilteredCars({
            cars: PrizeList?.Results || [],
          })
        );
      } catch (err) {
        console.error("Error while fetching makes, styles and prizes data");
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);
  const getRange = (placeholder: keyof Car) => {
    const uniqueRetailPrices = [
      ...new Set(ourCarsData?.carPrizes.PrizeList?.Results.map(carPrize => access(placeholder, carPrize))),
    ];
    const min = Math.min(...uniqueRetailPrices);
    const max = Math.max(...uniqueRetailPrices);
    return { min, max };
  };

  return isLoading ? (
    <OurCarsLoader />
  ) : (
    <div className="lg:mt-5">
      <div ref={ref}>
        <InTheHeadlight prizeCategory="dreamCar" classNames="dreamCarITH" />
        <InstantWinWinBannerAndTab competitionTypeId={competitionTypeId} />
      </div>
      <article className={`${styles.filtersAndCarsSection} dreamCarFiltersAndPrizes`}>
        {ourCarsData?.showFilters && (
          <OurCarsFilters
            makeList={ourCarsData?.makeList}
            styleList={ourCarsData?.styleList}
            ticketPriceRange={getRange("ActiveTicket.Price" as keyof Car)}
            rrpRange={getRange("RetailPriceGbp")}
            topMPHRange={getRange("TopSpeed")}
            zeroTo60Range={getRange("MphTime")}
            bhpRange={getRange("Power")}
            carPrizes={ourCarsData?.carPrizes.PrizeList?.Results}
            ithInView={inView}
          />
        )}
        <hr className="border-gray-300" />
        <CompetitionDefaultTicket.Provider
          value={{
            defaultTicketCount: ourCarsData?.carPrizes.DefaultTicketCount,
            showInstantWinText: ourCarsData?.carPrizes.ShowInstantWinText,
          }}>
          <OurCarPrizes
            isMobile={ourCarsData?.carPrizes.IsMobile}
            isActiveComp={ourCarsData?.carPrizes.HasActiveCompetition}
            isInstantWinActive={ourCarsData?.carPrizes.IsInstantWinActive}
          />
          <Modal
            isOpen={isBuyTicketModalVisible}
            title={""}
            closeModal={event => {
              event.stopPropagation();
              dispatch(publishModalVisibility({ isOpen: false }));
            }}
            modalClassNames="md:rounded-2xl xs:absolute xs:bottom-0 xs:w-full xs:rounded-b-none xs:rounded-t-2xl z-10 !px-4 pt-12 pb-8 lg:!px-8 lg:!pb-10 lg:!pt-12 !max-h-none"
            body={buyYourTicketsModalData}
          />
        </CompetitionDefaultTicket.Provider>
      </article>
    </div>
  );
};

export default OurCars;
